import { NeuralPrepData } from '../types/clientIntelligence';

export const formatNeuralPrepText = (prepData: any, clientName: string): string => {
  const currentDate = new Date().toLocaleDateString();
  const domain = prepData.domainAnalysis?.primaryDomain || 'General Business';
  
  let formattedText = `NEURAL PREP: ${clientName}\nGenerated: ${currentDate}\nDomain Intelligence: ${domain}\n\n`;
  
  // Personal Connection Points
  formattedText += "═══════════════════════════════════════════════════════════════\n";
  formattedText += "👥 PERSONAL CONNECTION POINTS\n";
  formattedText += "═══════════════════════════════════════════════════════════════\n\n";
  
  if (prepData.personalConnections && prepData.personalConnections.length > 0) {
    prepData.personalConnections.forEach((connection: any) => {
      formattedText += `• ${connection.topic}:\n  ${connection.context}\n`;
      if (connection.emotionalContext) {
        formattedText += `  Emotional Context: ${connection.emotionalContext}\n`;
      }
      formattedText += "\n";
    });
  }
  
  // AI-Enhanced Conversation Starters
  formattedText += "═══════════════════════════════════════════════════════════════\n";
  formattedText += "💼 AI-ENHANCED CONVERSATION STARTERS\n";
  formattedText += "═══════════════════════════════════════════════════════════════\n\n";
  
  if (prepData.conversationStarters && prepData.conversationStarters.length > 0) {
    prepData.conversationStarters.forEach((starter: any, index: number) => {
      formattedText += `${index + 1}. "${starter.question || starter}"\n`;
      if (starter.context) {
        formattedText += `   Context: ${starter.context}\n`;
      }
      if (starter.followUp) {
        formattedText += `   Follow-up: ${starter.followUp}\n`;
      }
      formattedText += "\n";
    });
  }
  
  // Strategic Points
  if (prepData.strategicPoints && prepData.strategicPoints.length > 0) {
    formattedText += "═══════════════════════════════════════════════════════════════\n";
    formattedText += "🎯 STRATEGIC TALKING POINTS\n";
    formattedText += "═══════════════════════════════════════════════════════════════\n\n";
    
    prepData.strategicPoints.forEach((point: any) => {
      formattedText += `• ${point.category} [${point.priority || 'Medium'} Priority]:\n`;
      formattedText += `  ${point.details}\n`;
      if (point.reasoning) {
        formattedText += `  Reasoning: ${point.reasoning}\n`;
      }
      formattedText += "\n";
    });
  }
  
  // Action Items
  if (prepData.actionItems && prepData.actionItems.length > 0) {
    formattedText += "═══════════════════════════════════════════════════════════════\n";
    formattedText += "⚡ IMMEDIATE ACTION ITEMS\n";
    formattedText += "═══════════════════════════════════════════════════════════════\n\n";
    
    prepData.actionItems.forEach((item: any, index: number) => {
      formattedText += `${index + 1}. ${item.title} [${item.priority || 'Medium'} Priority]\n`;
      formattedText += `   ${item.description}\n`;
      if (item.deadline) formattedText += `   Deadline: ${item.deadline}\n`;
      if (item.reasoning) formattedText += `   Reasoning: ${item.reasoning}\n`;
      formattedText += "\n";
    });
  }
  
  // AI Personality Insights
  if (prepData.aiInsights) {
    formattedText += "═══════════════════════════════════════════════════════════════\n";
    formattedText += "🎭 AI PERSONALITY INSIGHTS\n";
    formattedText += "═══════════════════════════════════════════════════════════════\n\n";
    
    if (prepData.aiInsights.personalityProfile) {
      formattedText += `Personality Profile:\n${prepData.aiInsights.personalityProfile}\n\n`;
    }
    
    if (prepData.aiInsights.communicationStyle) {
      formattedText += `Communication Style:\n${prepData.aiInsights.communicationStyle}\n\n`;
    }
    
    if (prepData.aiInsights.motivationFactors && prepData.aiInsights.motivationFactors.length > 0) {
      formattedText += "Motivation Factors:\n";
      prepData.aiInsights.motivationFactors.forEach((factor: string) => {
        formattedText += `• ${factor}\n`;
      });
      formattedText += "\n";
    }
    
    if (prepData.aiInsights.concernAreas && prepData.aiInsights.concernAreas.length > 0) {
      formattedText += "Concern Areas:\n";
      prepData.aiInsights.concernAreas.forEach((concern: string) => {
        formattedText += `• ${concern}\n`;
      });
      formattedText += "\n";
    }
    
    if (prepData.aiInsights.trustBuilders && prepData.aiInsights.trustBuilders.length > 0) {
      formattedText += "Trust Builders:\n";
      prepData.aiInsights.trustBuilders.forEach((builder: string) => {
        formattedText += `• ${builder}\n`;
      });
      formattedText += "\n";
    }
  }
  
  // Recent Client History
  if (prepData.recentHistory && prepData.recentHistory.length > 0) {
    formattedText += "═══════════════════════════════════════════════════════════════\n";
    formattedText += `📈 RECENT CLIENT HISTORY (Last ${prepData.recentHistory.length} Sessions)\n`;
    formattedText += "═══════════════════════════════════════════════════════════════\n\n";
    
    prepData.recentHistory.forEach((session: any, index: number) => {
      formattedText += `${index + 1}. ${session.title} (${session.date})`;
      if (session.sentiment) {
        formattedText += ` [${session.sentiment}]`;
      }
      formattedText += "\n";
      
      if (session.entities && session.entities.length > 0) {
        formattedText += `   Entities: ${session.entities.join(', ')}\n`;
      }
      
      if (session.topics && session.topics.length > 0) {
        formattedText += `   Topics: ${session.topics.join(', ')}\n`;
      }
      
      if (session.keyPoints && session.keyPoints.length > 0) {
        formattedText += "   Key Points:\n";
        session.keyPoints.forEach((point: string) => {
          formattedText += `   • ${point}\n`;
        });
      }
      formattedText += "\n";
    });
  }
  
  return formattedText;
};

export const generateEmailPrep = (prepData: any, clientName: string): void => {
  const subject = `Neural Prep - ${clientName} Meeting`;
  const fullBody = formatNeuralPrepText(prepData, clientName);
  
  // Create condensed email body
  let body = `NEURAL PREP: ${clientName}\n`;
  body += `Generated: ${new Date().toLocaleDateString()}\n\n`;
  
  // Personal Connections (condensed)
  body += "PERSONAL CONNECTION POINTS:\n";
  if (prepData.personalConnections && prepData.personalConnections.length > 0) {
    prepData.personalConnections.slice(0, 2).forEach((connection: any) => {
      body += `• ${connection.topic}: ${connection.context.substring(0, 80)}...\n`;
    });
  } else {
    body += "• Family updates and personal milestones\n• Health and wellness topics\n";
  }
  
  body += "\nCONVERSATION STARTERS:\n";
  if (prepData.conversationStarters && prepData.conversationStarters.length > 0) {
    prepData.conversationStarters.slice(0, 3).forEach((starter: any, index: number) => {
      const question = typeof starter === 'string' ? starter : starter.question || starter;
      body += `${index + 1}. "${question.substring(0, 100)}..."\n`;
    });
  }
  
  body += "\nACTION ITEMS:\n";
  if (prepData.actionItems && prepData.actionItems.length > 0) {
    prepData.actionItems.slice(0, 3).forEach((item: any, index: number) => {
      body += `${index + 1}. ${item.title} [${item.priority || 'Medium'}]\n`;
    });
  }
  
  body += `\nFor full details, see the neural prep in AddWise.\n\n--\nGenerated by AddWise Client Intelligence`;
  
  // Show email options modal
  const modalHtml = `
    <div id="emailModal" style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 10000;">
      <div style="background: white; padding: 24px; border-radius: 12px; max-width: 500px; width: 90%; box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);">
        <h3 style="margin: 0 0 16px 0; font-size: 18px; font-weight: 600; color: #1f2937;">Share Neural Prep</h3>
        <p style="margin: 0 0 20px 0; color: #6b7280; font-size: 14px;">Choose how you'd like to share this prep:</p>
        
        <div style="display: flex; flex-direction: column; gap: 12px;">
          <button onclick="openGmail()" style="padding: 12px 16px; background: #dc2626; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            📧 Gmail Web
          </button>
          <button onclick="openOutlook()" style="padding: 12px 16px; background: #0078d4; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            📧 Outlook Web
          </button>
          <button onclick="tryMailto()" style="padding: 12px 16px; background: #059669; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            📧 Default Email App
          </button>
          <button onclick="downloadPDF()" style="padding: 12px 16px; background: #dc2626; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            📄 Download as PDF
          </button>
          <button onclick="downloadPrep()" style="padding: 12px 16px; background: #7c3aed; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            💾 Download as Text File
          </button>
          <button onclick="copyToClipboard()" style="padding: 12px 16px; background: #374151; color: white; border: none; border-radius: 8px; cursor: pointer; font-weight: 500;">
            📋 Copy to Clipboard
          </button>
        </div>
        
        <button onclick="closeModal()" style="margin-top: 16px; padding: 8px 16px; background: #f3f4f6; color: #374151; border: none; border-radius: 6px; cursor: pointer; width: 100%;">
          Cancel
        </button>
      </div>
    </div>
  `;
  
  document.body.insertAdjacentHTML('beforeend', modalHtml);
  
  // Add event handlers
  (window as any).openGmail = () => {
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
    
    // Check URL length for Gmail (has stricter limits)
    if (gmailUrl.length > 2000) {
      console.log('Gmail URL too long, using condensed version');
      const condensedUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(condensedUrl, '_blank');
    } else {
      window.open(gmailUrl, '_blank');
    }
    closeModal();
  };
  
  (window as any).openOutlook = () => {
    const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?to=&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
    window.open(outlookUrl, '_blank');
    closeModal();
  };
  
  (window as any).tryMailto = () => {
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(fullBody)}`;
    window.location.href = mailtoUrl;
    closeModal();
  };
  
  (window as any).downloadPDF = () => {
    // Generate PDF using HTML content
    const pdfContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${subject}</title>
        <style>
          body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; color: #333; }
          h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
          h2 { color: #1e40af; margin-top: 30px; }
          .section { margin-bottom: 25px; }
          .highlight { background-color: #f0f9ff; padding: 15px; border-left: 4px solid #2563eb; margin: 10px 0; }
          ul { padding-left: 20px; }
          li { margin-bottom: 5px; }
          .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
        </style>
      </head>
      <body>
        <h1>${subject}</h1>
        <div class="highlight">
          <strong>Generated:</strong> ${new Date().toLocaleDateString()}<br>
          <strong>Client:</strong> ${clientName}
        </div>
        <pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${fullBody}</pre>
        <div class="footer">Generated by AddWise Client Intelligence</div>
      </body>
      </html>
    `;
    
    const blob = new Blob([pdfContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientName}_Neural_Prep_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    closeModal();
  };
  
  (window as any).downloadPrep = () => {
    const blob = new Blob([fullBody], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${clientName}_Neural_Prep_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    closeModal();
  };
  
  (window as any).copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(fullBody);
      alert('Neural prep copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      alert('Failed to copy to clipboard. Please try downloading instead.');
    }
    closeModal();
  };
  
  const closeModal = () => {
    const modal = document.getElementById('emailModal');
    if (modal) {
      modal.remove();
    }
  };
  
  (window as any).closeModal = closeModal;
};

export const generatePrintSheet = (prepData: any, clientName: string): void => {
  const subject = `Neural Prep - ${clientName}`;
  const fullBody = formatNeuralPrepText(prepData, clientName);
  
  // Generate HTML content for printing
  const pdfContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${subject}</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; color: #333; }
        h1 { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; }
        h2 { color: #1e40af; margin-top: 30px; }
        .section { margin-bottom: 25px; }
        .highlight { background-color: #f0f9ff; padding: 15px; border-left: 4px solid #2563eb; margin: 10px 0; }
        ul { padding-left: 20px; }
        li { margin-bottom: 5px; }
        .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; font-size: 12px; color: #6b7280; }
        @media print {
          body { margin: 20px; }
          .highlight { border-left: 2px solid #2563eb; }
        }
      </style>
    </head>
    <body>
      <h1>${subject}</h1>
      <div class="highlight">
        <strong>Generated:</strong> ${new Date().toLocaleDateString()}<br>
        <strong>Client:</strong> ${clientName}
      </div>
      <pre style="white-space: pre-wrap; font-family: Arial, sans-serif;">${fullBody}</pre>
      <div class="footer">Generated by AddWise Client Intelligence</div>
    </body>
    </html>
  `;
  
  const blob = new Blob([pdfContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${clientName}_Neural_Prep_Print_${new Date().toISOString().split('T')[0]}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const generateNeuralCheckIn = async (clientId: string): Promise<any> => {
  console.log(`🤖 Generating neural check-in for client ${clientId}`);
  
  try {
    // First attempt
    let response = await fetch(`/api/clients/${clientId}/neural-checkin`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' }
    });

    // If first attempt fails, wait 2 seconds and try again
    if (!response.ok) {
      console.log('First attempt failed, retrying in 2 seconds...');
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      response = await fetch(`/api/clients/${clientId}/neural-checkin`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    }

    const data = await response.json() as { 
      success: boolean; 
      checkinEmail?: any; 
      error?: string 
    };
    console.log('Neural Check-in API Response:', data);

    if (response.ok && data.success) {
      console.log('✅ Neural check-in generated successfully');
      return data.checkinEmail;
    } else {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }
  } catch (err) {
    console.error('❌ Neural check-in generation failed:', err);
    throw err;
  }
};

export const formatCheckInEmailText = (checkInData: any, clientName: string): string => {
  const {
    subject = `Checking in with you, ${clientName}`,
    greeting = `Hi ${clientName},`,
    personalNote = '',
    mainContent = '',
    callToAction = '',
    closing = 'Best regards,\nYour Financial Advisor'
  } = checkInData;

  return `Subject: ${subject}

${greeting}

${personalNote}

${mainContent}

${callToAction}

${closing}`;
};

export const generateCheckInEmailPrep = (checkInData: any, clientName: string): void => {
  const emailText = formatCheckInEmailText(checkInData, clientName);
  const subject = checkInData.subject || `Checking in with you, ${clientName}`;
  
  // Generate HTML content for email
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${subject}</title>
      <style>
        body { font-family: Arial, sans-serif; line-height: 1.6; margin: 40px; color: #333; }
        .email-container { max-width: 600px; margin: 0 auto; }
        .header { color: #2563eb; border-bottom: 2px solid #2563eb; padding-bottom: 10px; margin-bottom: 20px; }
        .section { margin-bottom: 20px; }
        .highlight { background-color: #f0f9ff; padding: 15px; border-left: 4px solid #2563eb; margin: 15px 0; }
        .signature { margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
        @media print {
          body { margin: 20px; }
          .no-print { display: none; }
        }
      </style>
    </head>
    <body>
      <div class="email-container">
        <h1 class="header">Neural Check-in Email</h1>
        
        <div class="section">
          <h2>Email Subject:</h2>
          <div class="highlight">${checkInData.subject || subject}</div>
        </div>
        
        <div class="section">
          <h2>Email Content:</h2>
          <div style="white-space: pre-line; padding: 15px; background-color: #f8fafc; border-radius: 8px;">
${emailText}
          </div>
        </div>
        
        <div class="signature">
          <p><strong>Generated:</strong> ${new Date().toLocaleString()}</p>
          <p><strong>Client:</strong> ${clientName}</p>
          <p><strong>Type:</strong> AI-Generated Check-in Email</p>
        </div>
      </div>
    </body>
    </html>
  `;

  // Create and download the HTML file
  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const fileName = `${clientName.replace(/\s+/g, '_')}_Check_In_Email_${new Date().toISOString().split('T')[0]}.html`;
  
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  
  console.log(`📧 Check-in email saved as: ${fileName}`);
}; 